import React from 'react'

import {
  Box,
  Button,
  Color,
  Display,
  FlexDirection,
  Image,
  Overflow,
  StepperButton,
  Text,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'

import { getProductImage } from './image'
import type { BasketUpsellProduct, BasketUpsellProductActionSource } from './types'
import { activateOnKeyboard, stopKeyboardActivationPropagation } from '../keyboard'

const ProductImage = withCitrusTheme(Image, () => ({
  objectFit: 'cover',
  objectPosition: 'center',
  imageRendering: 'auto',
}))

type ProductTileProps = {
  product: BasketUpsellProduct
  onClickProduct: (product: BasketUpsellProduct) => void
  onAddProduct: (productId: string, source: BasketUpsellProductActionSource) => void
  onRemoveProduct: (productId: string, source: BasketUpsellProductActionSource) => void
}

export const BasketUpsellProductTile = ({
  product,
  onAddProduct,
  onClickProduct,
  onRemoveProduct,
}: ProductTileProps) => {
  const image = getProductImage(product)
  const isUnavailable = !product.isAvailable || !!product.limitReached || product.isPending
  const handleTileClick = () => onClickProduct(product)
  const stopClickPropagation = (event: React.MouseEvent) => event.stopPropagation()

  return (
    <Box
      bg={Color.White}
      data-testid="basket-upsell-product-tile"
      display={Display.Flex}
      flexDirection={FlexDirection.Column}
      height="100%"
      overflowX={Overflow.Hidden}
      overflowY={Overflow.Hidden}
      onClick={handleTileClick}
      onKeyDown={(event) => activateOnKeyboard(event, handleTileClick)}
      role="button"
      style={{
        borderRadius: '4px',
        cursor: 'pointer',
      }}
      tabIndex={0}
    >
      <Box height="136px" width="100%">
        {image.src && (
          <ProductImage
            alt={image.alt}
            sizes="220px"
            src={image.src}
            srcSet={image.srcSet}
            width="100%"
            height="100%"
          />
        )}
      </Box>
      <Box
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        flexGrow={1}
        gap={2}
        paddingH={3}
        paddingV={3}
      >
        <Box flexGrow={1}>
          <Text color={Color.ColdGrey_800} fontWeight={700} size={3}>
            {product.title}
          </Text>
        </Box>
        <Box display={Display.Flex} flexDirection={FlexDirection.Column} gap={2}>
          <Text color={Color.ColdGrey_700} size={1}>
            {product.listPrice}
            {product.portion?.label && ` • ${product.portion.label}`}
          </Text>
          {product.qty > 0 ? (
            <Box onClick={stopClickPropagation} onKeyDown={stopKeyboardActivationPropagation}>
              <StepperButton
                count={product.qty}
                decrement={() => onRemoveProduct(product.id, 'basket_rth_carousel')}
                disableDecrement={product.isPending}
                disableIncrement={isUnavailable}
                fullWidth
                increment={() => onAddProduct(product.id, 'basket_rth_carousel')}
                incrementLabel="Add item"
              >
                {product.qty} added
              </StepperButton>
            </Box>
          ) : (
            <Button
              aria-label="Add product"
              disabled={isUnavailable}
              onClick={(event) => {
                event.stopPropagation()
                onAddProduct(product.id, 'basket_rth_carousel')
              }}
              onKeyDown={stopKeyboardActivationPropagation}
              width="100%"
            >
              Add
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  )
}
